import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { DAILY_REPORTS_LIST } from '../../api/constants';

// Async thunk for fetching daily reports
export const fetchDailyReports = createAsyncThunk(
  'dailyReport/fetchDailyReports',
  async ({ page = 1, limit = 10, startDate = null, endDate = null }, { rejectWithValue }) => {
    try {
      const requestData = {
        page,
        limit
      };
      
      if (startDate) {
        requestData.start_date = startDate;
      }

      if (endDate) {
        requestData.end_date = endDate;
      }
      
      const response = await apiHandler(DAILY_REPORTS_LIST, "POST", requestData);
      
      if (response.data?.response?.status === 200) {
        return response.data.data.data;
      } else {
        return rejectWithValue(response.data?.response?.message || 'Failed to fetch daily reports');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

const dailyReportSlice = createSlice({
  name: 'dailyReport',
  initialState: {
    // Reports list
    reportsList: [],
    
    // Loading state
    loading: false,
    
    // Error state
    error: null,
    
    // Meta data
    meta: {
      total: 0,
      per_page: 10,
      current_page: 1,
      last_page: 1
    },
    
    // Selected report for details view
    selectedReport: null
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    setSelectedReport: (state, action) => {
      state.selectedReport = action.payload;
    },
    clearSelectedReport: (state) => {
      state.selectedReport = null;
    },
    resetReportsList: (state) => {
      state.reportsList = [];
      state.meta = {
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch daily reports
      .addCase(fetchDailyReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyReports.fulfilled, (state, action) => {
        state.loading = false;
        
        // If it's page 1, replace the list; otherwise, append
        if (action.payload.meta.current_page === 1) {
          state.reportsList = action.payload.data;
        } else {
          state.reportsList = [...state.reportsList, ...action.payload.data];
        }
        
        state.meta = action.payload.meta;
      })
      .addCase(fetchDailyReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearErrors, 
  setSelectedReport,
  clearSelectedReport,
  resetReportsList 
} = dailyReportSlice.actions;

// Selectors
export const selectReportsList = (state) => state.dailyReport.reportsList;
export const selectLoading = (state) => state.dailyReport.loading;
export const selectError = (state) => state.dailyReport.error;
export const selectMeta = (state) => state.dailyReport.meta;
export const selectSelectedReport = (state) => state.dailyReport.selectedReport;

export default dailyReportSlice.reducer;
